import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft,
  FileText,
  Download,
  Archive,
  Send,
  Edit,
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Filter,
  Activity,
  Building2,
  User,
  AlertCircle,
  Undo2,
  Bell,
  AlertTriangle,
  UserPlus,
  UserMinus,
  Trash2,
  BookOpenCheck,
  Info,
  RefreshCw,
  Copy
} from "lucide-react";
import { toast } from "sonner";
import AddUsersToPolicyModal from "../components/AddUsersToPolicyModal";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Policy, Attachment } from "../types";
import FileAttachments from "../components/FileAttachments";
import { toSydneyDate, formatTimestamp } from "../utils/helpers";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

const getPolicy = async (id: any) => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/policies/${id}`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    }
  });

  const data = await response.json();

  return data.policy || null;
}

export default function PolicyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    getPolicyById,
    updatePolicy,
    users,
    departments,
    getUserById,
    getUserName,
    getDepartmentByCode,
    policyAssignments,
    getUserPolicySignatures,
    // removeUserFromPolicy,
    fetchPolicyAssignments,
    refreshPolicyAssignments,
    deletePolicy,
    archivePolicy,
    unarchivePolicy
  } = useData();
  const { currentUser, token } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [showRecallDialog, setShowRecallDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(true); // Start in loading state

  // Duplicate functionality state
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateTitle, setDuplicateTitle] = useState('');

  // Archive validation state
  const [archiveBlockedReason, setArchiveBlockedReason] = useState<string>('');
  const [blockedAssignments, setBlockedAssignments] = useState<any[]>([]);
  const [showArchiveBlockedDialog, setShowArchiveBlockedDialog] = useState(false);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [hasArchived, setHasArchived] = useState<boolean | null>(null);

  // const policy = getPolicyById(id || "");
  useEffect(() => {
    const fetchPolicy = async (id: any) => {
      if (id) {
        const policy = await getPolicy(id);

        if (policy) {
          setPolicy(policy);
        }
      }
    }

    fetchPolicy(id);
  }, [id, hasArchived])

  // Get assigned users - filter by current policy version for version-specific assignments
  const assignedUsers = useMemo(() => {
    if (!policy) return [];

    // Get all users who have assignment records for this policy's current version
    const usersWithAssignments = policyAssignments
      .filter(assignment =>
        assignment.policy?.id == policy.id &&
        assignment.policyVersion == policy.version
      )
      .map(assignment => assignment.user?.id);

    // Return users who have assignment records and are still active
    return users.filter(user =>
      !user.archived &&
      usersWithAssignments.includes(user.id)
    );
  }, [policy, users, policyAssignments]);

  // Get signature status for each user (for current version only)
  const userSignatureStatus = useMemo(() => {
    if (!policy) return [];

    return assignedUsers.map(user => {
      const assignment = policyAssignments.find(assign =>
        assign.policy?.id == policy.id &&
        assign.user?.id == user.id &&
        assign.policyVersion == policy.version
      );
      const signature = {
        signatureType: assignment?.signatureType,
        signatureData: assignment?.signatureData,
        signedDate: assignment?.signedDate,
        ipAddress: assignment?.ipAddress,
        userAgent: assignment?.userAgent
      };

      return {
        user,
        signature,
        assignment,
        status: signature ? "Signed" : (assignment?.readDate ? "Read" : "Not Read"),
        signedDate: signature?.signedDate,
        readDate: assignment?.readDate
      };
    });
  }, [policy, assignedUsers, policyAssignments]);

  // Filter users based on search and filters
  const filteredUsers = useMemo(() => {
    return userSignatureStatus.filter(item => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (!item.user.name.toLowerCase().includes(searchLower) &&
          !item.user.email.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== "all" && item.status !== statusFilter) {
        return false;
      }

      // Department filter
      if (departmentFilter !== "all" && item.user.department !== departmentFilter) {
        return false;
      }

      return true;
    });
  }, [userSignatureStatus, searchTerm, statusFilter, departmentFilter]);

  // Calculate completion statistics
  const stats = useMemo(() => {
    const total = assignedUsers.length;
    const signed = userSignatureStatus.filter(item => item.signature).length;
    const read = userSignatureStatus.filter(item => item.assignment?.readDate).length;

    return {
      total,
      signed,
      read,
      completionRate: total > 0 ? Math.round((signed / total) * 100) : 0,
      readingRate: total > 0 ? Math.round((read / total) * 100) : 0
    };
  }, [assignedUsers.length, userSignatureStatus]);

  // Check if archive is likely to be blocked
  const hasActiveAssignments = useMemo(() => {
    return userSignatureStatus.some(item =>
      item.status !== "Signed" && item.assignment // Has assignment but not signed
    );
  }, [userSignatureStatus]);

  // const isAdmin = currentUser?.userRole === "Admin" || currentUser?.userRole === "Director" || currentUser?.jobRole === "HSEQ Manager";

  // Fetch policy assignments and signatures when page loads (for admin view)
  useEffect(() => {
    if (policy) {
      console.log(`[PolicyDetail] Loading assignment data for policy ${policy.id}`);
      console.log(`[PolicyDetail] All assignments in context:`, policyAssignments.filter(a => a.policy?.id == policy.id).length);
      console.log(`[PolicyDetail] Current version assignments:`, policyAssignments.filter(a => a.policy?.id == policy.id && a.policyVersion == policy.version).length);
      console.log(`[PolicyDetail] Current version signatures:`, policyAssignments.filter(a => a.policy?.id == policy.id && a.policyVersion == policy.version && a.signatureType && a.signatureData).length);

      // Set loading state to prevent premature banner/button rendering
      setIsLoadingAssignments(true);

      // Load assignment data
      const loadAssignmentData = async () => {
        try {
          console.log(`[PolicyDetail] Starting assignment data load for policy ${policy.id}`);
          await fetchPolicyAssignments(policy.id);

          // Add delay to ensure state updates have propagated
          await new Promise(resolve => setTimeout(resolve, 100));

          console.log(`[PolicyDetail] Assignment data loaded for policy ${policy.id} v${policy.version}`, {
            policyId: policy.id,
            policyVersion: policy.version,
            totalAssignments: policyAssignments.length,
            totalSignatures: policyAssignments.filter(a => a.signatureType && a.signatureData).length,
            allAssignmentsForThisPolicy: policyAssignments.filter(a => a.policy?.id == policy.id).length,
            currentVersionAssignments: policyAssignments.filter(a => a.policy?.id == policy.id && a.policyVersion == policy.version).length,
            currentVersionSignatures: policyAssignments.filter(a => a.policy?.id == policy.id && a.policyVersion == policy.version && a.signatureType && a.signatureData).length,
            allVersions: [...new Set(policyAssignments.filter(a => a.policy?.id == policy.id).map(a => a.policyVersion))]
          });
        } catch (error) {
          console.error('Error loading assignment data:', error);
        } finally {
          setIsLoadingAssignments(false);
          console.log(`[PolicyDetail] Assignment loading complete for policy ${policy.id}`);
        }
      };

      loadAssignmentData();
    } else if (policy) {
      // For non-admin users, we don't need to load assignment data
      // but we should clear the loading state
      setIsLoadingAssignments(false);
    }
  }, [policy?.id]); // Functions intentionally omitted from deps to prevent infinite loop

  // Debug effect to track assignment data changes
  useEffect(() => {
    if (policy) {
      console.log(`[PolicyDetail] Assignment arrays updated:`, {
        policyId: policy.id,
        policyVersion: policy.version,
        totalAssignments: policyAssignments.length,
        totalSignatures: policyAssignments.filter(a => a.signatureType && a.signatureData).length,
        allAssignmentsForThisPolicy: policyAssignments.filter(a => a.policy?.id == policy.id),
        currentVersionAssignments: policyAssignments.filter(a => a.policy?.id == policy.id && a.policyVersion == policy.version),
        currentVersionSignatures: policyAssignments.filter(a => a.policy?.id == policy.id && a.policyVersion == policy.version && a.signatureType && a.signatureData),
        isLoadingAssignments
      });
    }
  }, [policyAssignments, policy?.id, isLoadingAssignments]);

  // Helper functions to determine policy assignment state
  const getPolicyAssignmentState = useMemo(() => {
    if (!policy) {
      console.log(`[PolicyDetail] Assignment state: none (no policy)`);
      return 'none';
    }

    // If still loading assignments, return 'loading' to prevent incorrect banner/button states
    if (isLoadingAssignments) {
      console.log(`[PolicyDetail] Assignment state: loading (still fetching data)`);
      return 'loading';
    }

    // If policyAssignments is empty and we haven't loaded assignment data yet, 
    // we should be in loading state to prevent incorrect permissions
    if (!policyAssignments || policyAssignments.length === 0) {
      console.log(`[PolicyDetail] Assignment state: loading (no assignments loaded yet)`);
      return 'loading';
    }

    const currentAssignments = policyAssignments.filter(a =>
      a.policy?.id === policy.id &&
      a.policyVersion === policy.version
    );

    console.log(`[PolicyDetail] Checking assignment state for policy ${policy.id} v${policy.version}:`, {
      isLoadingAssignments,
      totalAssignments: currentAssignments.length,
      totalSignatures: currentAssignments.filter(a => a.signatureType && a.signatureData).length,
      assignmentIds: currentAssignments.map(a => a.user?.id),
      signatureIds: currentAssignments.filter(a => a.signatureType && a.signatureData).map(a => a.user?.id),
      policyAssignmentsLength: policyAssignments.length,
      policySignaturesLength: currentAssignments.filter(a => a.signatureType && a.signatureData).length
    });

    if (currentAssignments.length === 0) {
      console.log(`[PolicyDetail] Assignment state: none (no assignments)`);
      return 'none';
    }

    // Check if policy requires signature to determine completion criteria
    const requiresSignature = policy.requireSignature;

    // Check if ALL assignments are complete
    const allComplete = currentAssignments.every(a => {
      if (requiresSignature) {
        return a.signatureType && a.signatureData;
      } else {
        return a.readDate;
      }
    });

    // Determine state:
    // - If all assignments are complete: 'completed'
    // - If there are assignments but not all complete: 'active' 
    // - If no assignments: 'none'
    const state = allComplete ? 'completed' : 'active';
    console.log(`[PolicyDetail] Assignment state: ${state}`, {
      allComplete,
      requiresSignature,
      totalAssignments: currentAssignments.length,
      completedAssignments: currentAssignments.filter(a => {
        if (requiresSignature) {
          return a.signatureType && a.signatureData;
        } else {
          return a.readDate;
        }
      }).length
    });

    return state;
  }, [policy, policyAssignments, isLoadingAssignments]);

  // Determine if editing is allowed
  const canEdit = useMemo(() => {
    if (!policy) return false;
    if (policy.status !== 'Active') return true;

    // While loading assignments, disable editing to prevent incorrect state
    if (getPolicyAssignmentState === 'loading') return false;

    const canEditResult = getPolicyAssignmentState !== 'active';
    console.log(`[PolicyDetail] Can edit: ${canEditResult}`, {
      policyStatus: policy.status,
      assignmentState: getPolicyAssignmentState,
    });
    return canEditResult;
  }, [policy, getPolicyAssignmentState]);

  // Determine if archiving is allowed
  const canArchive = useMemo(() => {
    if (!policy) return false;
    if (policy.status !== 'Active') return false;  // Only allow archiving of Active policies

    // While loading assignments, disable archiving to prevent incorrect state
    if (getPolicyAssignmentState === 'loading') return false;

    const canArchiveResult = getPolicyAssignmentState !== 'active';
    console.log(`[PolicyDetail] Can archive: ${canArchiveResult}`, {
      policyStatus: policy.status,
      assignmentState: getPolicyAssignmentState,
    });
    return canArchiveResult;
  }, [policy, getPolicyAssignmentState]);

  // Determine if deleting is allowed (stricter rules)
  const canDelete = useMemo(() => {
    if (!policy) return false;

    // Only allow deletion of Draft policies
    // Block deletion if policy has ever been assigned (to preserve audit trail)
    if (policy.status === 'Draft') {
      const allAssignments = policyAssignments.filter(a => a.policy?.id === policy.id);
      const hasNoAssignmentHistory = allAssignments.length === 0;

      console.log(`[PolicyDetail] Can delete: ${hasNoAssignmentHistory}`, {
        policyStatus: policy.status,
        hasAssignments: allAssignments.length > 0,
        allVersions: allAssignments.map(a => a.policyVersion),
      });
      return hasNoAssignmentHistory;
    }

    // Block deletion for Active and Archived policies (preserve audit trail)
    console.log(`[PolicyDetail] Can delete: false (${policy.status} policies cannot be deleted)`, {
      policyStatus: policy.status,
    });
    return false;
  }, [policy, policyAssignments]);

  // Banner Components
  const PolicyBanner = () => {
    if (!policy) return null;

    console.log(`[PolicyDetail] Banner render check:`, {
      policyStatus: policy.status,
      assignmentState: getPolicyAssignmentState,
      isLoadingAssignments,
      shouldShowBanner: policy.status === 'Active' && getPolicyAssignmentState === 'active'
    });

    // Don't show banner while loading assignment data to prevent incorrect state
    if (isLoadingAssignments) {
      console.log(`[PolicyDetail] Banner waiting for assignment data to load`);
      return null;
    }

    if (policy.status === 'Active' && getPolicyAssignmentState === 'active') {
      console.log(`[PolicyDetail] Rendering warning banner for policy ${policy.id}`);
      return (
        <div className="mb-6 flex items-center gap-2 rounded-md border border-yellow-500 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <span>You cannot edit this policy because it is currently assigned for signature. Please Recall the assignment if you need to make changes.</span>
        </div>
      );
    }

    if (policy.status === 'Active' && getPolicyAssignmentState === 'completed') {
      const lastCompletedAssignment = policyAssignments
        .filter(a => a.policy?.id === policy.id && a.readDate)
        .sort((a, b) => new Date(b.readDate!).getTime() - new Date(a.readDate!).getTime())[0];

      return (
        <div className="mb-6 flex items-start gap-2 rounded-md border border-blue-500 bg-blue-50 px-3 py-3 text-blue-900 dark:bg-blue-900/10 dark:text-blue-500">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <div className="font-medium text-blue-900 dark:text-blue-500">Information</div>
            <div className="text-sm text-blue-800 dark:text-blue-400">
              This policy has been signed previously under Assignment on{' '}
              {lastCompletedAssignment ? toSydneyDate(lastCompletedAssignment.readDate!) : 'unknown date'}.
              If you edit this policy, you may want to reassign it for a new signature.
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Early return if policy not found - AFTER all hooks are called
  if (!policy) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/policies">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Policies & Memos
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Policy Not Found</h1>
            <p className="text-muted-foreground">The requested policy could not be found</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle policy recall
  const handleRecallPolicy = async () => {
    if (!policy || !currentUser) return;

    setIsProcessing(true);
    try {
      // Call backend API to recall policy
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/policies/${policy.id}/recall`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to recall policy');
      }

      setShowRecallDialog(false);
      toast.success('Policy recalled successfully. All signatures have been removed and the policy is now in Draft status.');

      // Navigate back to policies management
      setTimeout(() => {
        navigate('/policies');
      }, 1500);

    } catch (error) {
      console.error('Error recalling policy:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to recall policy');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle policy archive
  const handleArchivePolicy = async () => {
    if (!policy || !currentUser) return;

    setIsProcessing(true);
    try {
      const success = await archivePolicy(policy.id, archiveReason);

      if (success) {
        setShowArchiveDialog(false);
        setArchiveReason("");
        setHasArchived(true);
        // Navigate back to policies management
        // setTimeout(() => {
        //   navigate('/policies');
        // }, 1500);
      }

    } catch (error: any) {
      console.error('Error archiving policy:', error);

      // Handle specific archive validation errors
      if (error.response?.status === 400 && error.response?.data?.activeAssignmentCount) {
        setArchiveBlockedReason(error.response.data.message);
        setBlockedAssignments(error.response.data.assignedUsers || []);
        setShowArchiveDialog(false);
        setShowArchiveBlockedDialog(true);
      } else if (error.message && error.message.includes('active assignments')) {
        // Handle cases where error comes through differently
        setArchiveBlockedReason(error.message);
        setShowArchiveDialog(false);
        setShowArchiveBlockedDialog(true);
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to archive policy');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // No local formatDate needed, using formatTimestamp from helpers

  // Handle policy delete
  const handleDeletePolicy = async () => {
    if (!policy || !currentUser) return;

    setIsProcessing(true);
    try {
      await deletePolicy(policy.id);
      setShowDeleteDialog(false);
      // Navigate back to policies management
      navigate('/policies');
    } catch (error) {
      console.error('Error deleting policy:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete policy');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle policy unarchive
  const handleUnarchivePolicy = async () => {
    if (!policy || !currentUser) return;

    setIsProcessing(true);
    try {
      await unarchivePolicy(policy.id);
      setShowUnarchiveDialog(false);
      setHasArchived(false);
    } catch (error) {
      console.error('Error unarchiving policy:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to unarchive policy');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDuplicate = async () => {
    if (!policy) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/policies/duplicate/${policy.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: duplicateTitle
        })
      });

      if (response.ok) {
        const newPolicy = await response.json();
        toast.success('Policy duplicated successfully');
        setDuplicateModalOpen(false);
        setDuplicateTitle('');
        // Navigate to policies page
        navigate('/policies');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to duplicate policy');
      }
    } catch (error) {
      console.error('Error duplicating policy:', error);
      toast.error('Failed to duplicate policy');
    }
  };

  const openDuplicateModal = () => {
    if (policy) {
      setDuplicateTitle(`${policy.title} (Copy)`);
      setDuplicateModalOpen(true);
    }
  };

  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link to="/policies">
            <Button variant="outline" size="icon" className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex flex-col">
            <h1 className="text-xl md:text-2xl font-bold leading-tight">{policy.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-muted-foreground">v{policy.version}</p>
              <Badge variant={policy.status === 'Active' ? 'default' : 'secondary'}>{policy.status}</Badge>
            </div>
          </div>
        </div>

        <Can I={ACTIONS.UPDATE} a={RESOURCES.POLICY}>
          <div className="w-full md:w-auto">
            <div className="grid grid-cols-2 md:flex md:items-center gap-2">
              {policy.status === 'Active' && (
                <>
                  <Button variant="outline" className="w-full" onClick={openDuplicateModal} disabled={isProcessing}><Copy className="h-4 w-4 mr-2" />Duplicate</Button>
                  <Can I={ACTIONS.ARCHIVE} a={RESOURCES.POLICY}>
                    <Button variant="outline" className="w-full" onClick={() => setShowArchiveDialog(true)} disabled={isProcessing || !canArchive}><Archive className="h-4 w-4 mr-2" />Archive</Button>
                  </Can>
                </>
              )}
              {policy.status === 'Draft' && (
                <>
                  <Button variant="outline" className="w-full" onClick={() => navigate(`/policy-edit/${policy.id}`)} disabled={isProcessing}><Edit className="h-4 w-4 mr-2" />Edit</Button>
                  <Can I={ACTIONS.DELETE} a={RESOURCES.POLICY}>
                    <Button variant="destructive" className="w-full" onClick={() => setShowDeleteDialog(true)} disabled={isProcessing || !canDelete}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
                  </Can>
                </>
              )}
              {policy.status === 'Archived' && (
                <Can I={ACTIONS.ARCHIVE} a={RESOURCES.POLICY}>
                  <Button variant="outline" className="w-full col-span-2" onClick={() => setShowUnarchiveDialog(true)} disabled={isProcessing}><RefreshCw className="h-4 w-4 mr-2" />Unarchive</Button>
                </Can>
              )}
            </div>
          </div>
        </Can>
      </div>

      {/* Banners */}
      <PolicyBanner />

      {/* "Active Policy - Edit Restricted" Banner - IMPROVED UI */}
      {policy.status === 'Active' && (
        <Can I={ACTIONS.UPDATE} a={RESOURCES.POLICY}>
          <Card className="bg-background">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Info className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div className="flex flex-col">
                  <p className="font-semibold">Active Policy - Edit Restricted</p>
                  <p className="text-sm text-muted-foreground">
                    To maintain integrity, active policies cannot be edited. Use "Duplicate" to create a new version.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Can>
      )}

      {/* "Archived" Banner - IMPROVED UI */}
      {policy.status === 'Archived' && (
        <Card className="bg-background">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <div className="flex flex-col">
                <p className="font-semibold">Archived</p>
                <p className="text-sm text-muted-foreground">
                  This policy is archived and read-only.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Document</CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold border-b pb-2 mb-3">Description</h3>
                <p className="text-muted-foreground">{policy.description}</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold border-b pb-2 mb-3">Policy Content / Instructions</h3>
                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground"
                  dangerouslySetInnerHTML={{ __html: policy.content.includes('<') ? policy.content : policy.content.replace(/\n/g, '<br />') }}
                />
              </div>
            </CardContent>
          </Card>

          {policy.attachments && policy.attachments.length > 0 && (
            <FileAttachments
              resourceType="policies"
              resourceId={id || ''}
              attachments={policy.attachments as Attachment[]}
              onAttachmentsChange={() => {}}
              disabled={true}
            />
          )}
        </div>

        {/* Sidebar Column */}
        <div className="lg:col-span-1 space-y-6">
          {/* --- POLICY DETAILS CARD (CORRECTED) --- */}
          <Card>
            <CardHeader>
              <CardTitle>Policy Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {/* Type and Policy Number */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-muted-foreground">Type</p>
                  <p>Policy</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Policy Number</p>
                  <p>{policy.policyNumber || 'N/A'}</p>
                </div>
              </div>
              {/* Status and Version */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-muted-foreground">Status</p>
                  <Badge variant={policy.status === 'Active' ? 'default' : 'secondary'}>{policy.status}</Badge>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Version</p>
                  <p>{policy.version}</p>
                </div>
              </div>
              {/* Signature Required */}
              <div>
                <p className="font-medium text-muted-foreground">Signature Required</p>
                <Badge variant={policy.requireSignature ? 'default' : 'secondary'} className={policy.requireSignature ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  {policy.requireSignature ? 'Yes' : 'No'}
                </Badge>
              </div>
              {/* Dates */}
              <div className="space-y-3 pt-2 border-t">
                <div>
                  <p className="font-medium text-muted-foreground">Effective Date</p>
                  <p className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {toSydneyDate(policy.effectiveDate)}</p>
                </div>
                {policy.reviewDate && (
                  <div>
                    <p className="font-medium text-muted-foreground">Review Date</p>
                    <p className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {toSydneyDate(policy.reviewDate)}</p>
                  </div>
                )}
                {policy.expiryDate && (
                  <div>
                    <p className="font-medium text-muted-foreground">Expiry Date</p>
                    <p className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {toSydneyDate(policy.expiryDate)}</p>
                  </div>
                )}
              </div>
              {/* People */}
              <div className="space-y-3 pt-2 border-t">
                <div>
                  <p className="font-medium text-muted-foreground">Created By</p>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {(() => {
                      const createdBy = policy.createdBy as any;
                      const user = typeof createdBy === 'object' ? createdBy : getUserById(createdBy);
                      return user ? <Link to={`/people/${user.id}`} className="text-primary hover:underline">{user.name}</Link> : "Unknown";
                    })()}
                  </div>
                </div>
                {policy.approvedBy && (
                  <div>
                    <p className="font-medium text-muted-foreground">Approved By</p>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {(() => {
                        const approvedBy = policy.approvedBy as any;
                        const user = typeof approvedBy === 'object' ? approvedBy : getUserById(approvedBy);
                        return user ? <Link to={`/people/${user.id}`} className="text-primary hover:underline">{user.name}</Link> : "Unknown";
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(policy.activityLogs || []).slice(0, 3).map(log => {
                  const user = getUserById(typeof log.user !== 'string' ? log.user.id : "");
                  return (
                    <div key={log.id} className="flex items-start gap-3">
                      <Avatar className="h-8 w-8 mt-1"><AvatarFallback>{user?.name.charAt(0) || '?'}</AvatarFallback></Avatar>
                      <div>
                        <p className="text-sm">{log.changeDescription}</p>
                        <p className="text-xs text-muted-foreground">{user?.name} • {formatTimestamp(log.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
                {(policy.activityLogs || []).length === 0 && <p className="text-sm text-center text-muted-foreground py-4">No activity recorded.</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recall Confirmation Dialog */}
      <Dialog open={showRecallDialog} onOpenChange={setShowRecallDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Recall Policy
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to recall "{policy.title}"?
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h4 className="font-medium text-amber-800 mb-2">This action will:</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Change the policy status to "Draft"</li>
                <li>• Delete all existing digital signatures</li>
                <li>• Remove the policy from all users' "My Policies" list</li>
                <li>• Allow you to edit and republish the policy</li>
              </ul>
            </div>

            <div className="flex justify-between gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowRecallDialog(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRecallPolicy}
                disabled={isProcessing}
                className="flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                    Recalling...
                  </>
                ) : (
                  <>
                    <Undo2 className="h-4 w-4" />
                    Recall Policy
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={(open) => {
        setShowArchiveDialog(open);
        if (!open) setArchiveReason("");
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-blue-500" />
              Archive Policy
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to archive "{policy.title}"?
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="font-medium text-blue-800 mb-2">This action will:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Change the policy status to "Archived"</li>
                <li>• You can undo this action at any time</li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label htmlFor="archiveReason">Reason for archiving (optional)</Label>
              <Textarea
                id="archiveReason"
                placeholder="Enter the reason for archiving this policy..."
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="resize-none"
                rows={3}
              />
            </div>

            <div className="flex justify-between gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowArchiveDialog(false);
                  setArchiveReason("");
                }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleArchivePolicy}
                disabled={isProcessing}
                className="flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                    Archiving...
                  </>
                ) : (
                  <>
                    <Archive className="h-4 w-4" />
                    Archive Policy
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Blocked Dialog */}
      <Dialog open={showArchiveBlockedDialog} onOpenChange={setShowArchiveBlockedDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Cannot Archive Policy
            </DialogTitle>
            <DialogDescription>
              This policy cannot be archived because it has active assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
              <div className="text-sm text-amber-800">
                {archiveBlockedReason}
              </div>
            </div>

            {blockedAssignments.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Users with pending assignments:</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {blockedAssignments.map((assignment) => (
                    <div key={assignment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                      <div>
                        <div className="font-medium">{assignment.name}</div>
                        <div className="text-sm text-gray-600">{assignment.email}</div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-xs">
                          {assignment.status}
                        </Badge>
                        <div className="text-xs text-gray-500 mt-1">
                          Assigned: {toSydneyDate(assignment.assignedDate)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
              <div className="text-sm text-blue-800">
                <strong>Recommendation:</strong> Wait for users to complete their assignments, or manually cancel the assignments before archiving this policy.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveBlockedDialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowArchiveBlockedDialog(false);
              // Navigate to assignments view to manage them
              navigate('/policies?tab=assignments');
            }}>
              Manage Assignments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Policy
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete "{policy.title}"? This action cannot be undone.
            </p>

            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
              <h4 className="font-medium text-destructive mb-2">Warning:</h4>
              <ul className="text-sm text-destructive/80 space-y-1">
                <li>• This will permanently delete the policy</li>
                <li>• All associated data will be removed</li>
                <li>• This action cannot be reversed</li>
              </ul>
            </div>

            <div className="flex justify-between gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeletePolicy}
                disabled={isProcessing}
                className="flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete Policy
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unarchive Confirmation Dialog */}
      <Dialog open={showUnarchiveDialog} onOpenChange={setShowUnarchiveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-500" />
              Unarchive Policy
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to unarchive "{policy.title}"?
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="font-medium text-blue-800 mb-2">This action will:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Change the policy status back to "Active"</li>
                <li>• Make the policy visible to users again</li>
              </ul>
            </div>

            <div className="flex justify-between gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowUnarchiveDialog(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUnarchivePolicy}
                disabled={isProcessing}
                className="flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                    Unarchiving...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Unarchive Policy
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Policy Modal */}
      <Dialog open={duplicateModalOpen} onOpenChange={setDuplicateModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Duplicate Policy</DialogTitle>
            <DialogDescription>
              Create a copy of "{policy?.title}" as a new draft policy. The new policy will start at version 1.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="duplicate-title" className="text-right">
                Title *
              </Label>
              <Input
                id="duplicate-title"
                value={duplicateTitle}
                onChange={(e) => setDuplicateTitle(e.target.value)}
                className="col-span-3"
                placeholder="Enter title for new policy"
              />
            </div>
            <div className="col-span-4 text-sm text-muted-foreground bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800">New Policy Information:</span>
              </div>
              <ul className="mt-2 text-blue-700 space-y-1">
                <li>• Version will be set to 1 (new policy)</li>
                <li>• Status will be set to Draft</li>
                <li>• All content will be copied from the original</li>
                <li>• No assignments will be copied</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDuplicate} disabled={!duplicateTitle.trim()}>
              Duplicate Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}